import { useEffect, useState } from "react";
import { Ban, Loader2, Smartphone, Wifi } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

export const blockPoints = (id) => api.put(`/admin/users/${id}/exclusions`, { points: true });

const fmtDay = (s) => (s ? new Date(s).toLocaleDateString("de-DE") : "–");

export function LinkedAccounts({ accounts, onBlock, testId }) {
  return (
    <ul className="space-y-1.5" data-testid={testId}>
      {accounts.map((a) => (
        <li key={a.user_id} className="flex items-center gap-2.5 text-sm" data-testid={`linked-${a.user_id}`}>
          <UserAvatar userId={a.user_id} name={a.name} picture={a.picture} size="w-7 h-7" text="text-xs" noStory crown={false} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{a.name} <span className="text-[10px] font-bold uppercase text-slate-400">· {a.via}</span></p>
            <p className="text-[11px] text-slate-400 truncate">{a.email} · seit {fmtDay(a.created_at)} · {(a.points || 0).toLocaleString("de-DE")} P.</p>
          </div>
          {a.no_points || a.banned ? <span className="text-[11px] font-bold text-rose-500">{a.banned ? "Gesperrt" : "Punkte gesperrt"}</span>
            : <button onClick={() => onBlock([a])} data-testid={`linked-block-${a.user_id}`} className="h-8 px-3 rounded-full border border-rose-200 text-rose-600 text-xs font-bold">Sperren</button>}
        </li>
      ))}
    </ul>
  );
}

export function FakeClustersAdmin({ onBlocked }) {
  const [groups, setGroups] = useState(null);
  useEffect(() => { api.get("/admin/points/fake-clusters").then(({ data }) => setGroups(data)).catch((e) => { setGroups([]); toast.error(formatErr(e)); }); }, []);
  const block = async (accs) => {
    try {
      await Promise.all(accs.map((a) => blockPoints(a.user_id)));
      const ids = new Set(accs.map((a) => a.user_id));
      setGroups((gs) => gs.map((g) => ({ ...g, accounts: g.accounts.map((a) => (ids.has(a.user_id) ? { ...a, no_points: true } : a)) })));
      accs.forEach((a) => onBlocked({ ...a, no_points: true }));
      toast.success(accs.length > 1 ? `${accs.length} Konten gesperrt` : `Punkte-Sammeln für ${accs[0].name} gesperrt`);
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-4" data-testid="fake-clusters-admin">
      <div>
        <h2 className="text-lg font-extrabold">Mögliche Fake-Konten</h2>
        <p className="text-sm text-slate-500">Mehrere Konten vom selben Gerät oder Netzwerk. „Gleiches Gerät“ ist ein starkes Zeichen, „Gleiches Netzwerk“ kann auch Familie oder ein gemeinsames WLAN sein.</p>
      </div>
      {!groups ? <Loader2 className="w-5 h-5 animate-spin text-rose-400 mx-auto" /> : groups.length === 0 ? <p className="text-sm text-slate-400 text-center py-3" data-testid="fake-clusters-empty">Keine verknüpften Konten gefunden 👍</p> : (
        <ul className="space-y-3" data-testid="fake-clusters-list">
          {groups.map((g, i) => {
            const open = g.accounts.filter((a) => !a.no_points && !a.banned);
            const Icon = g.kind === "device" ? Smartphone : Wifi;
            return (
              <li key={i} className="rounded-2xl border border-rose-100 p-4 space-y-3" data-testid={`fake-cluster-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold flex items-center gap-1.5"><Icon className={`w-4 h-4 ${g.kind === "device" ? "text-rose-500" : "text-slate-400"}`} /> {g.accounts.length} Konten · {g.kind === "device" ? "Gleiches Gerät" : "Gleiches Netzwerk"}</p>
                  {open.length > 0 && <button onClick={() => block(open)} data-testid={`fake-cluster-block-all-${i}`} className="h-8 px-3 shrink-0 whitespace-nowrap rounded-full bg-rose-600 text-white text-xs font-bold inline-flex items-center gap-1"><Ban className="w-3.5 h-3.5" /> Alle sperren</button>}
                </div>
                <LinkedAccounts accounts={g.accounts} onBlock={block} testId={`fake-cluster-accounts-${i}`} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
